import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Inject, inject, Injectable, OnInit, Optional, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WorkExperience } from '../../../models/workExperience';
import { AutocompleteSelectComponent } from '../../shared/autocomplete-select/autocomplete-select.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ProgressiveTypeaheadDirective } from '../../../directives/progressive-typeahead.directive';
import { JobPosition } from '../../../models/jobPosition';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { WorkExperienceCompany } from '../../../models/workExperienceCompany';
import { Country } from '../../../models/country';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { CompanyService } from '../../../services/companies/company.service';
import { OpbCompanyService } from '../../../services/opb-company.service';
import { Observable, forkJoin, map, of, catchError } from 'rxjs';
import { DateAdapter, MAT_DATE_FORMATS, NativeDateAdapter } from '@angular/material/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { HotToastService } from '@ngxpert/hot-toast';

const DD_MM_YYYY_FORMAT = {
  parse: { dateInput: 'DD/MM/YYYY' },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

function endDateAndPresentMutuallyExclusive(group: AbstractControl): ValidationErrors | null {
  const endDate = group.get('endDate')?.value;
  const isPresent = !!group.get('isPresent')?.value;
  return endDate && isPresent ? { endDateAndPresent: true } : null;
}

@Injectable()
class CustomDateAdapter extends NativeDateAdapter {
  override parse(value: any): Date | null {
    if (!value) return null;

    if (typeof value === 'string') {
      const parts = value.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);

        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          return new Date(year, month, day);
        }
      }
    }

    return super.parse(value);
  }

  override format(date: Date, displayFormat: Object): string {
    if (!date) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }
}

type CompanySource = 'workExperienceCompany' | 'company' | 'opbCompany';

interface CompanySearchResult {
  id: string;
  name: string;
  displayName: string;
  companyType: CompanySource;
  countryId: string | null;
  countryName: string | null;
  systemReserved: boolean;
}

const COMPANY_SOURCE_LABELS: Record<CompanySource, string> = {
  workExperienceCompany: 'Work Experience',
  company: 'Companies List',
  opbCompany: 'OPB Companies',
};

function buildDisplayName(name: string, source: CompanySource): string {
  return `${name} · ${COMPANY_SOURCE_LABELS[source]}`;
}

@Component({
  selector: 'app-work-experience-selection-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    CommonModule,
    AutocompleteSelectComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ProgressiveTypeaheadDirective,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatDatepickerModule,
    DragDropModule,
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DD_MM_YYYY_FORMAT }
  ],
  templateUrl: './work-experience-selection-dialog.component.html',
  styleUrl: './work-experience-selection-dialog.component.scss'
})
export class WorkExperienceSelectionDialogComponent implements OnInit, AfterViewInit {
  private lutService = inject(LutService);
  private lookupService = inject(LookupDataService);
  private companyService = inject(CompanyService);
  private opbCompanyService = inject(OpbCompanyService);
  private confirmationDialogService = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);

  @ViewChild('jobPositionAutocomplete') jobPositionAutocomplete?: AutocompleteSelectComponent;
  @ViewChild('companyAutocomplete') companyAutocomplete?: AutocompleteSelectComponent;

  selectedWorkExperience: WorkExperience | null = null;
  form: FormGroup;
  countries: Country[] = [];
  newCompanyCountryControl = new FormControl<Country | null>(null);

  isEditMode: boolean;
  private originalCompany: any = null;
  private originalCompanyType: WorkExperience['companyType'] | undefined;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<WorkExperienceSelectionDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private data: WorkExperience | null,
  ) {
    this.isEditMode = !!data;
    this.form = this.fb.group({
      company: ['', Validators.required],
      jobPosition: ['', Validators.required],
      startDate: [null as Date | null],
      endDate: [null as Date | null],
      isPresent: [false]
    }, { validators: endDateAndPresentMutuallyExclusive });
  }

  ngOnInit(): void {
    this.lookupService.getTableStream<Country>('countries').subscribe(data => {
      if (data) this.countries = [...data].sort((a, b) => a.name.localeCompare(b.name));
    });

    this.form.get('isPresent')?.valueChanges.subscribe((isPresent: boolean) => {
      const endDate = this.form.get('endDate');
      if (!endDate) return;
      if (isPresent) {
        endDate.setValue(null, { emitEvent: false });
        endDate.disable({ emitEvent: false });
      } else {
        endDate.enable({ emitEvent: false });
      }
    });

    if (this.data) {
      const source = this.companyTypeToSource(this.data.companyType);
      const c = this.data.company;
      const initialCompany: CompanySearchResult | null = c
        ? {
            id: c.id,
            name: c.name,
            displayName: source ? buildDisplayName(c.name, source) : c.name,
            companyType: source ?? 'workExperienceCompany',
            countryId: c.countryId ?? null,
            countryName: c.countryName ?? null,
            systemReserved: !!c.systemReserved,
          }
        : null;
      this.originalCompany = initialCompany;
      this.originalCompanyType = this.data.companyType;
      this.form.patchValue({
        company: initialCompany,
        jobPosition: this.data.position,
        startDate: this.toDate(this.data.startDate),
        endDate: this.toDate(this.data.endDate),
        isPresent: !!this.data.isPresent
      });
    }
  }

  ngAfterViewInit(): void {
    if (!this.data) return;
    queueMicrotask(() => {
      const name = this.data?.company?.name ?? '';
      const source = this.companyTypeToSource(this.data?.companyType);
      const companyDisplay = name && source ? buildDisplayName(name, source) : name;
      const positionName = this.data?.position?.name ?? '';
      this.companyAutocomplete?.searchControl.setValue(companyDisplay, { emitEvent: false });
      this.jobPositionAutocomplete?.searchControl.setValue(positionName, { emitEvent: false });
    });
  }

  private companyTypeToSource(t: WorkExperience['companyType'] | undefined): CompanySource | null {
    if (t === 'workExperienceCompany' || t === 'company' || t === 'opbCompany') return t;
    return null;
  }

  private toDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  compareCountryById = (a: Country | null, b: Country | null): boolean => !!a && !!b && a.id === b.id;

  searchJobPositions = (searchTerm: string): Observable<JobPosition[]> => {
    return this.lutService.searchJobPositions({
      pageNumber: 1,
      pageSize: 1000,
      sortBy: 'name',
      ascending: true,
      name: searchTerm
    }).pipe(
      map(response => {
        const term = (searchTerm ?? '').trim().toLowerCase();
        if (!term) return response.data;
        const exact: JobPosition[] = [];
        const rest: JobPosition[] = [];
        for (const jp of response.data) {
          ((jp.name ?? '').toLowerCase() === term ? exact : rest).push(jp);
        }
        return exact.length ? [...exact, ...rest] : response.data;
      })
    );
  };

  searchCompanies = (searchTerm: string): Observable<CompanySearchResult[]> => {
    const workExp$ = this.lutService.searchWorkExperienceCompanies({
      pageNumber: 1, pageSize: 1000, sortBy: 'name', ascending: true, name: searchTerm
    }).pipe(
      map(r => (r.data ?? []).map<CompanySearchResult>(c => ({
        id: c.id,
        name: c.name,
        displayName: buildDisplayName(c.name, 'workExperienceCompany'),
        companyType: 'workExperienceCompany',
        countryId: c.countryId ?? null,
        countryName: c.countryName ?? null,
        systemReserved: !!c.systemReserved,
      }))),
      catchError(() => of<CompanySearchResult[]>([]))
    );

    const companies$ = this.companyService.search({
      pageNumber: 1, pageSize: 500, sortBy: 'CompanyName', ascending: true, companyName: searchTerm
    }).pipe(
      map(r => (r.data ?? []).map<CompanySearchResult>(c => ({
        id: c.id,
        name: c.companyName,
        displayName: buildDisplayName(c.companyName, 'company'),
        companyType: 'company',
        countryId: c.countryId ?? null,
        countryName: c.countryName ?? null,
        systemReserved: false,
      }))),
      catchError(() => of<CompanySearchResult[]>([]))
    );

    const opb$ = this.opbCompanyService.searchOpbCompanies({
      pageNumber: 1, pageSize: 500, sortBy: 'name', ascending: true, name: searchTerm
    }).pipe(
      map(r => (r.data ?? []).map<CompanySearchResult>(c => ({
        id: c.id,
        name: c.name,
        displayName: buildDisplayName(c.name, 'opbCompany'),
        companyType: 'opbCompany',
        countryId: c.countryId ?? null,
        countryName: c.countryName ?? null,
        systemReserved: false,
      }))),
      catchError(() => of<CompanySearchResult[]>([]))
    );

    return forkJoin([workExp$, companies$, opb$]).pipe(
      map(([workExp, companies, opb]) => {
        const merged = [...workExp, ...companies, ...opb];
        const term = (searchTerm ?? '').trim().toLowerCase();
        if (!term) return merged;
        const exact: CompanySearchResult[] = [];
        const rest: CompanySearchResult[] = [];
        for (const c of merged) {
          ((c.name ?? '').toLowerCase() === term ? exact : rest).push(c);
        }
        rest.sort((a, b) => a.name.localeCompare(b.name));
        return [...exact, ...rest];
      })
    );
  };

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    if (this.form.valid) {
      const isPresent = !!this.form.get('isPresent')?.value;
      const selected = this.form.get('company')?.value;
      const companyUnchanged = this.isEditMode && selected === this.originalCompany;
      const companyType: WorkExperience['companyType'] = companyUnchanged
        ? this.originalCompanyType
        : (selected?.companyType ?? 'workExperienceCompany');
      const company: WorkExperienceCompany = selected
        ? {
            id: selected.id,
            name: selected.name,
            countryId: selected.countryId ?? null,
            countryName: selected.countryName ?? null,
            systemReserved: !!selected.systemReserved,
          }
        : selected;
      this.selectedWorkExperience = {
        id: this.isEditMode && this.data ? this.data.id : crypto.randomUUID(),
        company,
        companyType,
        position: this.form.get('jobPosition')?.value,
        startDate: this.form.get('startDate')?.value ?? null,
        endDate: isPresent ? null : (this.form.get('endDate')?.value ?? null),
        isPresent
      };
      this.dialogRef.close(this.selectedWorkExperience);
    }
  }

  onJobPositionSelected(jobPosition: JobPosition): void {
    this.form.get('jobPosition')?.setValue(jobPosition);
  }

  onCompanySelected(company: CompanySearchResult): void {
    this.form.get('company')?.setValue(company);
  }

  get canAddNewJobPosition(): boolean {
    const ac = this.jobPositionAutocomplete;
    if (!ac) return false;
    if (ac.isLoading) return false;

    const value = (ac.searchControl.value ?? '').toString().trim();
    if (value.length < 3) return false;

    const selected: JobPosition | null = this.form.get('jobPosition')?.value || null;
    if (selected && selected.name === value) return false;

    // If the typed text exactly matches an existing result, no need to add.
    const valueLower = value.toLowerCase();
    const exactMatch = ac.filteredOptions.some(o => (o?.name ?? '').toLowerCase() === valueLower);
    if (exactMatch) return false;

    // Show when there are no results, or when the user has dismissed the panel
    // (clicked elsewhere / Esc) without picking one of the partial matches.
    return ac.filteredOptions.length === 0 || !ac.isPanelOpen;
  }

  onAddNewJobPosition(): void {
    const ac = this.jobPositionAutocomplete;
    const name = (ac?.searchControl.value ?? '').toString().trim();
    if (!name) return;

    this.confirmationDialogService.openConfirmationDialog(
      'Add Job Position',
      `No job position matched "${name}". Add it as a new job position?`,
      'Add',
      'Cancel',
      'add',
      'clear'
    ).subscribe(confirmed => {
      if (!confirmed) return;
      this.lutService.createJobPosition({ name, systemReserved: false }).subscribe({
        next: (created) => {
          this.form.get('jobPosition')?.setValue(created);
          if (this.jobPositionAutocomplete) {
            this.jobPositionAutocomplete.filteredOptions = [created];
            this.jobPositionAutocomplete.totalFilteredCount = 1;
            this.jobPositionAutocomplete.searchControl.setValue(created.name, { emitEvent: false });
          }
          this.toast.success(`Job position "${created.name}" added`, {
            iconTheme: { primary: 'var(--mat-sys-primary)', secondary: '#FFF' }
          });
        },
        error: (error) => {
          console.error('Error creating job position:', error);
          this.toast.error('Failed to add job position');
        }
      });
    });
  }

  get canAddNewWorkExperienceCompany(): boolean {
    const ac = this.companyAutocomplete;
    if (!ac) return false;
    if (ac.isLoading) return false;

    const value = (ac.searchControl.value ?? '').toString().trim();
    if (value.length < 3) return false;

    const selected: CompanySearchResult | null = this.form.get('company')?.value || null;
    if (selected && (selected.displayName === value || selected.name === value)) return false;

    const valueLower = value.toLowerCase();
    const exactMatch = ac.filteredOptions.some(o =>
      (o?.name ?? '').toLowerCase() === valueLower ||
      (o?.displayName ?? '').toLowerCase() === valueLower
    );
    if (exactMatch) return false;

    return ac.filteredOptions.length === 0 || !ac.isPanelOpen;
  }

  onAddNewWorkExperienceCompany(): void {
    const ac = this.companyAutocomplete;
    const name = (ac?.searchControl.value ?? '').toString().trim();
    const country = this.newCompanyCountryControl.value;
    if (!name || !country) return;

    this.confirmationDialogService.openConfirmationDialog(
      'Add Company',
      `No company matched "${name}". Add it as a new company in ${country.name}?`,
      'Add',
      'Cancel',
      'add',
      'clear'
    ).subscribe(confirmed => {
      if (!confirmed) return;
      this.lutService.createWorkExperienceCompany({
        name,
        countryId: country.id,
        countryName: country.name,
        systemReserved: false
      }).subscribe({
        next: (created) => {
          const result: CompanySearchResult = {
            id: created.id,
            name: created.name,
            displayName: buildDisplayName(created.name, 'workExperienceCompany'),
            companyType: 'workExperienceCompany',
            countryId: created.countryId ?? null,
            countryName: created.countryName ?? null,
            systemReserved: !!created.systemReserved,
          };
          this.form.get('company')?.setValue(result);
          if (this.companyAutocomplete) {
            this.companyAutocomplete.filteredOptions = [result];
            this.companyAutocomplete.totalFilteredCount = 1;
            this.companyAutocomplete.searchControl.setValue(result.displayName, { emitEvent: false });
          }
          this.newCompanyCountryControl.reset();
          this.toast.success(`Company "${created.name}" added`, {
            iconTheme: { primary: 'var(--mat-sys-primary)', secondary: '#FFF' }
          });
        },
        error: (error) => {
          console.error('Error creating work experience company:', error);
          this.toast.error('Failed to add company');
        }
      });
    });
  }
}
